// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/PlanHeader.yaml
// Generated: 2026-06-02T14:10:11.394Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { planHeaderService } from '@/services/finance/PlanHeaderService';
import { CreatePlanHeader, UpdatePlanHeader, PlanHeader } from '@/types/finance/PlanHeader';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for PlanHeader
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const planheaderKeys = {
  all: (tenantId?: string | null) => ['planheaders', tenantId] as const,
  lists: (tenantId?: string | null) => [...planheaderKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...planheaderKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...planheaderKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...planheaderKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all PlanHeaders
 */
export function usePlanHeaders(options?: Omit<UseQueryOptions<PlanHeader[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<PlanHeader[], Error>({
    queryKey: planheaderKeys.lists(tenantId),
    queryFn: () => planHeaderService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single PlanHeader by ID
 */
export function usePlanHeader(
  id: string,
  options?: Omit<UseQueryOptions<PlanHeader | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<PlanHeader | undefined, Error>({
    queryKey: planheaderKeys.detail(id, tenantId),
    queryFn: () => planHeaderService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new PlanHeader
 */
export function useCreatePlanHeader() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanHeader, Error, CreatePlanHeader, { previousPlanHeaders?: PlanHeader[] }>({
    mutationFn: (data: CreatePlanHeader) => planHeaderService.create(data),
    onMutate: async (newPlanHeader) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planheaderKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanHeaders = queryClient.getQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newPlanHeader, id: (newPlanHeader as { id?: string }).id ?? 'temp-' + Date.now() } as PlanHeader
      ]);

      return { previousPlanHeaders };
    },
    onError: (_err, _newPlanHeader, context) => {
      // Rollback on error
      if (context?.previousPlanHeaders) {
        queryClient.setQueryData(planheaderKeys.lists(tenantId), context.previousPlanHeaders);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planheaderKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing PlanHeader
 */
export function useUpdatePlanHeader() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<PlanHeader, Error, { id: string; data: UpdatePlanHeader }, { previousPlanHeader?: PlanHeader; previousPlanHeaders?: PlanHeader[] }>({
    mutationFn: ({ id, data }) => planHeaderService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planheaderKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: planheaderKeys.lists(tenantId) });

      // Snapshot previous values
      const previousPlanHeader = queryClient.getQueryData<PlanHeader>(planheaderKeys.detail(id, tenantId));
      const previousPlanHeaders = queryClient.getQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<PlanHeader>(planheaderKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as PlanHeader);

      // Optimistically update list
      queryClient.setQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as PlanHeader : item))
      );

      return { previousPlanHeader, previousPlanHeaders };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousPlanHeader) {
        queryClient.setQueryData(planheaderKeys.detail(id, tenantId), context.previousPlanHeader);
      }
      if (context?.previousPlanHeaders) {
        queryClient.setQueryData(planheaderKeys.lists(tenantId), context.previousPlanHeaders);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planheaderKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: planheaderKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a PlanHeader
 */
export function useDeletePlanHeader() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousPlanHeaders?: PlanHeader[] }>({
    mutationFn: (id: string) => planHeaderService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planheaderKeys.lists(tenantId) });

      // Snapshot previous value
      const previousPlanHeaders = queryClient.getQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<PlanHeader[]>(planheaderKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousPlanHeaders };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousPlanHeaders) {
        queryClient.setQueryData(planheaderKeys.lists(tenantId), context.previousPlanHeaders);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: planheaderKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSavePlanHeader() {
  const createMutation = useCreatePlanHeader();
  const updateMutation = useUpdatePlanHeader();

  return {
    mutate: (params: { id?: string; data: CreatePlanHeader | UpdatePlanHeader }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdatePlanHeader }, options);
      } else {
        return createMutation.mutate(params.data as CreatePlanHeader, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
